import { useState, useEffect, useRef, useCallback } from "react";
import { ARTICLES } from "../data.js";
import { useAuth } from "../lib/AuthContext.jsx";
import { supabase } from "../lib/supabaseClient.js";
import { pathFor, parsePath } from "./router.js";

// Leaving the screen mid-quiz (a fiche link, a browser refresh) used to
// lose quizIdx/quizAnswer/quizChecked/quizCorrect entirely — they only ever
// lived in memory (todo.md, P2 "Session de quiz non persistée"). Guest-
// friendly localStorage round-trip, same pattern as notes: not Supabase,
// since an *in-progress* quiz isn't a completed session worth a table row
// (quiz_sessions already covers finished attempts).
const QUIZ_PROGRESS_KEY = "netdoc-quiz-progress";
function restoreQuizProgress() {
  try {
    const p = JSON.parse(window.localStorage.getItem(QUIZ_PROGRESS_KEY) || "null");
    if (!p || !p.quizDomainId) return {};
    return {
      quizDomainId: p.quizDomainId, quizIdx: p.quizIdx || 0,
      quizAnswer: p.quizAnswer ?? null, quizChecked: !!p.quizChecked, quizCorrect: p.quizCorrect || 0,
    };
  } catch {
    return {};
  }
}

export const initialState = {
  screen: "home", articleId: "bgp-attrs", platform: "IOS-XE", copied: false,
  searchOpen: false, query: "", hoverId: null,
  bookmarks: {}, done: {},
  courseId: "encor", moduleIdx: 0,
  answer: null, checked: false, indexFilter: "Tous",
  toolsMenuOpen: false, articleTab: "article", notes: {}, profileName: "",
  calcTab: "subnet", cidrInput: "10.20.30.5/22", splitPrefix: null,
  vlsmBase: "10.40.0.0/20", vlsmNeeds: "500, 200, 60, 30, 2, 2",
  mtuSel: { dot1q: true, vxlan: true },
  treeId: "no-route", treePath: [], xlateQuery: "",
  compareId: "ospf-isis", diagramId: "spine-leaf", diagramKey: null,
  graphFocus: "bgp-attrs",
  // A new user hasn't reviewed anything yet: no article starts pre-seeded
  // in a Leitner box. reviseView's boxOf() defaults a missing entry to 1
  // (freshest/most-due), so an empty object is already the right neutral
  // starting point.
  boxes: {},
  reviseSeen: 0, cardFlipped: false, reviseForce: false, certifId: "encor",
  // Active quiz session (course module quiz, or a Certif domain practice —
  // same mechanism, entered from either screen): quizDomainId is
  // `${courseId}:${moduleIdx}`, matching QUIZ_BANK's domainId.
  quizDomainId: null, quizIdx: 0, quizAnswer: null, quizChecked: false, quizCorrect: 0,
  // Completed sessions: seeded from Supabase on sign-in, appended to
  // locally on every completion (signed in or guest — guests just don't
  // persist across reloads).
  quizSessions: [],
  // Ephemeral sync feedback (not user data, but part of `s` since every
  // screen builder already reads `s` through `ctx` — no separate context
  // needed for something this small). syncLoading/syncError cover the
  // initial pull-on-sign-in; toast covers one-off write failures.
  syncLoading: false, syncError: null, toast: null,
};

// The shared "engine": central state, the Supabase sync effects/writers, and
// the navigation/lookup helpers every per-screen view builder in
// src/screens/ needs. This stays one hook rather than being split further —
// bookmarks/notes/done/boxes/quizSessions are genuinely read from more than
// one screen (e.g. `done` drives both Course and the Certif mastery score),
// so the state itself can't be isolated per screen without lifting it back
// up here anyway. What *is* isolated is the per-screen presentation logic:
// each src/screens/*.js file is a plain function that reads this core and
// returns its own slice of the view-model, instead of one 800-line hook
// building all of them inline.
export function useCore() {
  const [s, setS] = useState(() => {
    if (typeof window === "undefined") return initialState;
    const fromUrl = parsePath(window.location.pathname);
    const base = fromUrl ? { ...initialState, ...fromUrl } : { ...initialState, screen: "notfound" };
    return { ...base, ...restoreQuizProgress() };
  });
  const inputRef = useRef(null);
  const { user } = useAuth();
  const prevUserId = useRef(user?.id ?? null);
  const toastTimer = useRef(null);
  const lastFocusedRef = useRef(null);

  // this.setState({ ...partial }) equivalent (shallow merge, supports updater fn)
  const patch = useCallback((partial) => {
    setS((prev) => ({ ...prev, ...(typeof partial === "function" ? partial(prev) : partial) }));
  }, []);

  // One-off feedback for a failed write (bookmark/note/lesson/box/quiz sync)
  // — logged either way, but a silent console.error is invisible to the
  // person who just lost a write. Self-dismisses; dismissToast (below)
  // lets the banner close it early.
  const showToast = useCallback((message) => {
    patch({ toast: message });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => patch({ toast: null }), 6000);
  }, [patch]);
  const dismissToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    patch({ toast: null });
  }, [patch]);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // Signed-in users: pull their saved bookmarks/notes/lesson progress/
  // spaced-repetition boxes from Supabase and merge them over the local
  // defaults. Signed-out visitors (or no Supabase project configured) keep
  // today's guest behavior untouched. syncLoading/syncError let the UI show
  // "Synchronisation…" and surface a partial-failure banner instead of the
  // previous silent no-op on error.
  useEffect(() => {
    if (!supabase || !user) return;
    let cancelled = false;
    patch({ syncLoading: true, syncError: null });
    (async () => {
      const [
        { data: bm, error: bmErr }, { data: nt, error: ntErr }, { data: lp, error: lpErr },
        { data: rb, error: rbErr }, { data: qs, error: qsErr }, { data: pr, error: prErr },
      ] = await Promise.all([
        supabase.from("bookmarks").select("article_id").eq("user_id", user.id),
        supabase.from("notes").select("article_id, body").eq("user_id", user.id),
        supabase.from("lesson_progress").select("course_id, lesson_n, done").eq("user_id", user.id),
        supabase.from("revise_boxes").select("article_id, box").eq("user_id", user.id),
        supabase.from("quiz_sessions").select("domain_id, score_pct, correct_count, total_count, completed_at").eq("user_id", user.id).order("completed_at", { ascending: true }),
        supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle(),
      ]);
      if (cancelled) return;
      const errors = [bmErr, ntErr, lpErr, rbErr, qsErr, prErr].filter(Boolean);
      if (errors.length) console.error("[netdoc] sync-on-signin failed:", errors.map((e) => e.message).join("; "));

      // Notes: single source of truth once signed in (todo.md, P2) — from
      // here on, saveNotes() stops writing to localStorage, Supabase is
      // authoritative. But a guest who wrote notes *before* signing in has
      // them only in localStorage — migrate those up now (local wins over
      // remote on a same-article conflict, since it's what this person
      // just typed a moment ago in this browser) rather than silently
      // discarding them, then drop the local copy so it can't drift out of
      // sync again on a later guest session in the same browser.
      const remoteNotes = Object.fromEntries((nt || []).map((r) => [r.article_id, r.body]));
      let localNotes = {};
      try { localNotes = JSON.parse(window.localStorage.getItem("netdoc-notes") || "{}") || {}; } catch { /* ignore */ }
      const mergedNotes = { ...remoteNotes, ...localNotes };
      const toMigrate = Object.entries(localNotes).filter(([id, body]) => remoteNotes[id] !== body);
      if (toMigrate.length) {
        Promise.all(toMigrate.map(([article_id, body]) =>
          supabase.from("notes").upsert({ user_id: user.id, article_id, body, updated_at: new Date().toISOString() })
        )).then((results) => {
          const failed = results.filter((r) => r.error);
          if (failed.length) console.error("[netdoc] note migration failed for", failed.length, "note(s):", failed[0].error.message);
        });
      }
      try { window.localStorage.removeItem("netdoc-notes"); } catch { /* ignore */ }

      patch((prev) => ({
        syncLoading: false,
        syncError: errors.length
          ? "Certaines données n'ont pas pu être récupérées depuis votre compte (favoris, notes, progression…). Réessayez de recharger la page."
          : null,
        bookmarks: { ...prev.bookmarks, ...Object.fromEntries((bm || []).map((r) => [r.article_id, true])) },
        notes: mergedNotes,
        done: { ...prev.done, ...Object.fromEntries((lp || []).map((r) => [r.course_id + ":" + r.lesson_n, r.done])) },
        boxes: { ...prev.boxes, ...Object.fromEntries((rb || []).map((r) => [r.article_id, r.box])) },
        quizSessions: (qs || []).map((r) => ({ domainId: r.domain_id, scorePct: r.score_pct, correctCount: r.correct_count, totalCount: r.total_count, completedAt: r.completed_at })),
        profileName: pr?.display_name || "",
      }));
    })();
    return () => { cancelled = true; };
  }, [user, patch]);

  // Signing out on a shared machine shouldn't leave the previous user's
  // bookmarks/notes/progress on screen for the next visitor — reset them to
  // guest defaults the moment `user` drops from signed-in to signed-out.
  useEffect(() => {
    if (!prevUserId.current || user?.id) { prevUserId.current = user?.id ?? null; return; }
    prevUserId.current = null;
    patch({
      bookmarks: {}, notes: {}, done: {},
      boxes: initialState.boxes, quizSessions: [],
      syncLoading: false, syncError: null, profileName: "",
    });
  }, [user, patch]);

  // URL sync (todo.md, P1 "Navigation par URL"): the fields below are
  // exactly the ones that determine `pathFor(s)` (see router.js) — every
  // nav()/go()/goCourse()/setModule()/practiceDomain()/... call already
  // changes one of these through patch(), so this is the one place that
  // needs to know about routing, not each call site. Pushes a new history
  // entry (not replace) so Back steps through fiches/modules one at a
  // time, like a reader would expect.
  useEffect(() => {
    if (s.screen === "notfound") return; // keep the bad URL in the bar as-is
    const path = pathFor(s);
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
    // Deliberately narrow: pathFor(s) only reads these fields, and listing
    // `s` itself would re-run (and re-check, though not re-push) this on
    // every unrelated keystroke/toggle in the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.screen, s.articleId, s.courseId, s.moduleIdx, s.calcTab, s.compareId, s.diagramId, s.graphFocus, s.certifId]);

  // Browser Back/Forward changes window.location without React knowing —
  // re-derive state from the new URL. pathFor(next-state) then round-trips
  // to the same pathname the popstate just landed on, so the effect above
  // is a no-op (no extra pushState, no loop).
  useEffect(() => {
    const onPopState = () => {
      const fromUrl = parsePath(window.location.pathname);
      patch(fromUrl ? { ...fromUrl, searchOpen: false, toolsMenuOpen: false } : { screen: "notfound" });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [patch]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); openSearch(); }
      if (e.key === "Escape") patch({ searchOpen: false });
    };
    window.addEventListener("keydown", onKey);
    try {
      const raw = window.localStorage.getItem("netdoc-notes");
      if (raw) patch({ notes: JSON.parse(raw) });
    } catch {
      // localStorage unavailable (private browsing, quota) — guest notes just won't restore.
    }
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the in-progress quiz answer/position mirrored to localStorage (see
  // restoreQuizProgress above) so leaving the screen mid-quiz doesn't lose
  // it. Skipped once quizDomainId is unset — there's never a "clear
  // progress" action in the UI today, so this only actually no-ops before
  // the very first quiz of a session.
  useEffect(() => {
    if (!s.quizDomainId) return;
    try {
      window.localStorage.setItem(QUIZ_PROGRESS_KEY, JSON.stringify({
        quizDomainId: s.quizDomainId, quizIdx: s.quizIdx, quizAnswer: s.quizAnswer,
        quizChecked: s.quizChecked, quizCorrect: s.quizCorrect,
      }));
    } catch {
      // localStorage unavailable — progress just won't survive leaving the screen.
    }
  }, [s.quizDomainId, s.quizIdx, s.quizAnswer, s.quizChecked, s.quizCorrect]);

  // Focus restitution for the search palette (todo.md, P1): whichever
  // element had focus when ⌘K/openSearch fired gets it back once the
  // palette closes, however it closed (Escape, click-outside, Enter on a
  // result) — all three just flip s.searchOpen to false via patch(),
  // giving one place to watch instead of three.
  useEffect(() => {
    if (!s.searchOpen && lastFocusedRef.current) {
      lastFocusedRef.current.focus?.();
      lastFocusedRef.current = null;
    }
  }, [s.searchOpen]);

  // Signed in: Supabase is the single source of truth for notes (migrated
  // from localStorage on sign-in, see the sync effect above) — stop writing
  // to localStorage so the two can't drift apart again. Guest: localStorage
  // is the only persistence available.
  const saveNotes = (notes) => {
    patch({ notes });
    if (user) return;
    try {
      window.localStorage.setItem("netdoc-notes", JSON.stringify(notes));
    } catch {
      // localStorage unavailable — note stays in memory for this session only.
    }
  };

  // Writes a single article's note through to Supabase when signed in;
  // guest mode keeps relying on saveNotes' localStorage fallback above.
  const syncNote = (articleId, body) => {
    if (!supabase || !user) return;
    supabase.from("notes").upsert({ user_id: user.id, article_id: articleId, body, updated_at: new Date().toISOString() }).then(({ error }) => {
      if (error) { console.error("[netdoc] note sync failed:", error.message); showToast("La note n'a pas pu être enregistrée en ligne — elle reste sur cet appareil."); }
    });
  };

  const syncBookmark = (articleId, on) => {
    if (!supabase || !user) return;
    const q = on
      ? supabase.from("bookmarks").upsert({ user_id: user.id, article_id: articleId })
      : supabase.from("bookmarks").delete().eq("user_id", user.id).eq("article_id", articleId);
    q.then(({ error }) => {
      if (error) { console.error("[netdoc] bookmark sync failed:", error.message); showToast("Le favori n'a pas pu être synchronisé."); }
    });
  };

  // lessonN here already encodes the module (moduleIdx*100 + n, see
  // lessonKey below) so it stays unique within a course without a
  // separate module column in lesson_progress.
  const syncLesson = (courseId, lessonN, done) => {
    if (!supabase || !user) return;
    supabase.from("lesson_progress").upsert({ user_id: user.id, course_id: courseId, lesson_n: lessonN, done, updated_at: new Date().toISOString() }).then(({ error }) => {
      if (error) { console.error("[netdoc] lesson sync failed:", error.message); showToast("La progression de la leçon n'a pas pu être synchronisée."); }
    });
  };

  const syncQuizSession = (domainId, correctCount, totalCount) => {
    if (!supabase || !user) return;
    const scorePct = Math.round((correctCount / totalCount) * 100);
    supabase.from("quiz_sessions").insert({ user_id: user.id, domain_id: domainId, score_pct: scorePct, correct_count: correctCount, total_count: totalCount }).then(({ error }) => {
      if (error) { console.error("[netdoc] quiz session sync failed:", error.message); showToast("Le résultat du quiz n'a pas pu être enregistré."); }
    });
  };

  const syncBox = (articleId, box) => {
    if (!supabase || !user) return;
    supabase.from("revise_boxes").upsert({ user_id: user.id, article_id: articleId, box, updated_at: new Date().toISOString() }).then(({ error }) => {
      if (error) { console.error("[netdoc] revise box sync failed:", error.message); showToast("La révision n'a pas pu être synchronisée."); }
    });
  };

  // profiles row already exists per-user (created by the handle_new_user
  // trigger on signup, see supabase/migrations/0001_init.sql) — this only
  // ever updates it, never inserts.
  const saveProfileName = (name) => {
    patch({ profileName: name });
    if (!supabase || !user) return;
    supabase.from("profiles").update({ display_name: name }).eq("id", user.id).then(({ error }) => {
      if (error) { console.error("[netdoc] profile name sync failed:", error.message); showToast("Le nom n'a pas pu être enregistré."); }
    });
  };

  const nav = (screen) => patch({ screen, searchOpen: false, toolsMenuOpen: false });

  const dueLabel = (box) => {
    const days = [0, 1, 3, 7, 16, 35][box] || 0;
    if (s.reviseForce) return "en file";
    return box <= 2 ? "à revoir" : "dans " + days + " j";
  };

  const openSearch = () => {
    lastFocusedRef.current = document.activeElement;
    patch({ searchOpen: true, query: "" });
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 30);
  };
  const go = (id) => patch({ screen: "article", articleId: id, searchOpen: false, copied: false, toolsMenuOpen: false, articleTab: "article" });

  // Module 0 lesson 3 and module 1 lesson 3 both have n=3 within their own
  // module — combine into one int so a single (course_id, lesson_n) row
  // stays unique per lesson without a separate module column.
  const lessonKey = (moduleIdx, n) => moduleIdx * 100 + n;
  const doneKey = (courseId, moduleIdx, n) => courseId + ":" + lessonKey(moduleIdx, n);

  const goCourse = (courseId) => patch({ screen: "course", courseId, moduleIdx: 0, searchOpen: false, toolsMenuOpen: false });
  const setModule = (moduleIdx) => patch({ moduleIdx });

  const startQuiz = (domainId) => patch({ quizDomainId: domainId, quizIdx: 0, quizAnswer: null, quizChecked: false, quizCorrect: 0 });
  const practiceDomain = (domainId) => {
    const [courseId, moduleIdxStr] = domainId.split(":");
    patch({ screen: "course", courseId, moduleIdx: Number(moduleIdxStr), searchOpen: false, toolsMenuOpen: false });
    startQuiz(domainId);
  };

  // Ranked, not just filtered: an exact/leading title match should surface
  // before a hit buried in the CLI examples of an unrelated article.
  const results = () => {
    const q = s.query.trim().toLowerCase();
    if (!q) return ARTICLES;
    const score = (a) => {
      const title = a.title.toLowerCase();
      if (title === q) return 100;
      if (title.startsWith(q)) return 80;
      if (title.includes(q)) return 60;
      if (a.domain.toLowerCase().includes(q)) return 40;
      if (a.intro.toLowerCase().includes(q)) return 20;
      if (Object.values(a.cli).join(" ").toLowerCase().includes(q)) return 10;
      return 0;
    };
    return ARTICLES.map((a) => ({ a, sc: score(a) }))
      .filter((x) => x.sc > 0)
      .sort((x, y) => y.sc - x.sc)
      .map((x) => x.a);
  };

  // Progress across every module of a course (used by the Home "Vos
  // parcours" cards, which show the whole track, not just the module
  // currently open).
  const courseProgress = (c) => {
    let done = 0, total = 0, next = null;
    c.modules.forEach((m, mi) => {
      m.lessons.forEach((l) => {
        total++;
        if (s.done[doneKey(c.id, mi, l.n)]) done++;
        else if (!next) next = l.title;
      });
    });
    return { pct: total ? Math.round((done / total) * 100) : 0, next };
  };

  const pctForDomain = (domainId) => {
    const sessions = s.quizSessions.filter((x) => x.domainId === domainId);
    return sessions.length ? sessions[sessions.length - 1].scorePct : 0;
  };
  const attemptedDomain = (domainId) => s.quizSessions.some((x) => x.domainId === domainId);

  return {
    s, patch, inputRef, user,
    saveNotes, syncNote, syncBookmark, syncLesson, syncQuizSession, syncBox, saveProfileName,
    nav, dueLabel, openSearch, go, lessonKey, doneKey, goCourse, setModule,
    startQuiz, practiceDomain, results, courseProgress, pctForDomain, attemptedDomain,
    dismissToast,
  };
}
